// React
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Styles
import '../styles/Mission.css';

// Components
import MissionHeader from '../components/MissionHeader';
import MainButton from '../components/MainButton';

// Data
import { contentData } from '../assets/data/contentData';

// GSAP
import gsap from 'gsap';
import { useTimer } from '../contextes/useTimer';

// Sound Context
import { useSound } from '../contextes/SoundContext';

// Page de mission
const Mission = () => {
    const location = useLocation();
    const { playSound } = useSound();
    const missionNumber = location.pathname.split('/').pop();
    const missionContent = contentData.missions.find(
        (mission) => mission.id === Number(missionNumber),
    );
    const navigate = useNavigate();
    // 1. Créer une référence pour l'élément DOM
    const el = useRef<HTMLDivElement>(null);
    const { resetTimer, setInitialTime, setTime } = useTimer();

    useEffect(() => {
        // Ciblage de tous les éléments à animer dans le conteneur principal.
        const elementsToHide = [
            '.title',
            '.subtitle',
            '.agent-illustration',
            '.mission-page-agent',
            '.mission-page-description',
        ];

        const elementsToFadeIn = ['.button-mission-page'];

        const elementsToScale = ['.mission-page-agente-name'];


        // 2. Cacher et repositionner tous les éléments AVANT l'animation
        // C'est souvent la cause d'une animation qui "ne marche pas" (elle est juste déjà finie).
        gsap.set(elementsToHide, { opacity: 0, y: 20 });
        gsap.set(elementsToFadeIn, { opacity: 0 });
        gsap.set(elementsToScale, { opacity: 0, scale: 0 });

        if (el.current) {
            const tl = gsap.timeline({});

            // 3. Animer la séquence
            tl.to('.subtitle', { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' })
                .to('.title', { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }, '-=0.6')
                .to(
                    '.agent-illustration',
                    { opacity: 1, scale: 1, duration: 0.6, ease: 'back.out(1.7)' },
                    // '-=0.4'
                )
                .to(
                    '.mission-page-agent',
                    { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
                    '-=0.3',
                )
                .to(
                    '.mission-page-agente-name',
                    { opacity: 1, scale: 1, duration: 0.6, ease: 'back.out(1.7)' },
                    // '-=0.3'
                )
                .to(
                    '.mission-page-description',
                    { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
                    // '-=0.2'
                )
                .to(
                    '.button-mission-page',
                    { opacity: 1, duration: 0.6, ease: 'power2.out' }, // '-=0.1'
                );
        }
    }, [missionNumber]);

    // Jouer le son d'ambiance et réinitialiser le timer à l'entrée de la page
    useEffect(() => {
        playSound('ambiance');
        resetTimer();
    }, []);

    // Gérer le clic sur le bouton "Commencer"
    const handleCommencerClick = () => {
        if(missionContent?.duree) {
            setInitialTime(missionContent.duree * 60);
            setTime(missionContent.duree * 60);
            navigate('game');
            return;
        }
        navigate('parametres');
    };

    return (
        <div
            className={`mission-container mission-container-${missionNumber}`}
            ref={el}
        >
            <MissionHeader
                showSettingsButton={false}
                showSoundIcon={true}
                showTimer={false}
                soundButtonVariant={missionNumber === '2' ? 'tertiary' : 'primary'}
                backButtonVariant={missionNumber === '1' ? 'primary' : missionNumber === '0' ? 'secondary' : 'tertiary'}
            />
            <div className='mission-page-title'>
                {missionNumber != '0' && <div className={`subtitle subtitle-${missionNumber}`}>Mission {missionNumber}</div>}
                <h2 className='title'>{missionContent?.title}</h2>
            </div>
            <img
                src={missionContent?.illustration}
                alt={missionContent?.title}
                className={missionNumber === '1' ? 'agent-illustration' : 'agent-illustration-2'}
            />
            <div className='mission-page-agent-container'>
                <p className='mission-page-agent'>{missionContent?.subtitle}</p>
                <p className='mission-page-agente-name'>{missionContent?.agentName}</p>
            </div>
            <p
                className={`mission-page-description mission-page-description-${missionNumber}`}
                dangerouslySetInnerHTML={{
                    __html: missionContent?.description?.replace(/\n/g, '<br />') || '',
                }}
            />

            <div className='button-mission-page'>
                <MainButton
                    text='Commencer'
                    onClick={handleCommencerClick}
                    backgroundColor={missionNumber === '1' || missionNumber === '0' ? 'secondary' : 'primary'}
                />
            </div>
        </div>
    );
};

export default Mission;
